/**
 * Remembers where you were on each bottom-nav tab.
 *
 * Tapping Cards navigated to a bare "/cards" every time, which threw away the
 * tab you had open, the era you had drilled into, the set you were browsing and
 * your scroll position. Coming back always dropped you at the top of your own
 * collection, which is what made moving between tabs feel like the app was
 * reloading itself.
 *
 * State lives in sessionStorage, so it survives navigation within a session and
 * starts clean on a cold launch, which is the right behaviour for "where was I"
 * without becoming a stale bookmark days later.
 */

const KEY = 'cardchase_nav_memory';

function read() {
  try {
    return JSON.parse(window.sessionStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
}

function write(state) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable - navigation still works, it just forgets */
  }
}

/**
 * Record where the user is on a tab before they leave it.
 * @param {string} tabId  bottom-nav tab id, e.g. "cards"
 * @param {string} url    full path plus query, e.g. "/cards?tab=all&era=sv"
 * @param {number} [scrollTop]
 */
export function rememberTab(tabId, url, scrollTop = 0) {
  if (!tabId || !url) return;
  const state = read();
  state[tabId] = { url, scrollTop, at: Date.now() };
  write(state);
}

/**
 * Where to send someone tapping this tab.
 * @returns {string} the remembered url, or the tab's default path
 */
export function recallTab(tabId, defaultPath) {
  const entry = read()[tabId];
  if (!entry?.url) return defaultPath;
  // Only ever restore within the same tab's own section, so a stale entry can
  // never bounce someone somewhere unrelated.
  if (!entry.url.startsWith(defaultPath)) return defaultPath;
  return entry.url;
}

/** The scroll offset to restore for a tab, if we have one. */
export function recallScroll(tabId) {
  return read()[tabId]?.scrollTop || 0;
}

/** Forget a tab, e.g. when its underlying content is no longer valid. */
export function forgetTab(tabId) {
  const state = read();
  delete state[tabId];
  write(state);
}

export default { rememberTab, recallTab, recallScroll, forgetTab };
